import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Settings, Hammer, Lock } from "lucide-react";
import { ADMIN_AREAS, ADMIN_SETTINGS_PAGES, pageHref, areaForPath, letterOf, slug } from "@/components/layout/adminNavMap";

export function AdminAreaNav({ onNavigate }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => areaForPath(pathname));
  const [settingsOpen, setSettingsOpen] = useState(false);
  useEffect(() => { const a = areaForPath(pathname); if (a) setOpen(a); }, [pathname]);

  return (
    <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="admin-area-nav">
      {ADMIN_AREAS.map((a, ai) => {
        const isOpen = open === a.key;
        return (
          <div key={a.key}>
            <div className={`flex items-center rounded-xl transition-colors ${isOpen ? "bg-[#1F1F1F] text-white" : "text-gray-700 hover:bg-gray-100"}`}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : a.key)}
                className="flex-1 flex items-center gap-3 px-3 h-11 text-sm font-black tracking-tight"
                data-testid={`nav-area-${a.key}`}
                aria-expanded={isOpen}
              >
                <span className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-black text-white" style={{ background: a.color }}>{letterOf(ai)}</span>
                <span className="flex-1 text-left">{a.label}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <NavLink to={`/admin/home/${a.key}`} onClick={onNavigate} className="px-2 h-11 inline-flex items-center opacity-60 hover:opacity-100" title={`Apri la schermata ${a.label}`} data-testid={`nav-area-home-${a.key}`}>
                <a.icon className="w-4 h-4" style={{ color: isOpen ? "white" : a.color }} />
              </NavLink>
            </div>
            {isOpen && (
              <div className="mt-1 mb-2 ml-3 pl-3 border-l-2 space-y-2" style={{ borderColor: a.color }} data-testid={`nav-area-groups-${a.key}`}>
                {a.groups.map((g, gi) => (
                  <div key={g.label}>
                    <NavLink to={`/admin/home/${a.key}/${slug(g.label)}`} onClick={onNavigate} className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400 hover:text-[#1F1F1F] px-2 pt-1 flex items-center gap-1.5">
                      <span className="text-[#1F1F1F]">{letterOf(gi)}</span>{g.label}
                    </NavLink>
                    {g.pages.map((p, pi) => (
                      <NavLink
                        key={pageHref(p) + p.label}
                        to={pageHref(p)}
                        onClick={onNavigate}
                        end={p.to === "/admin"}
                        className={({ isActive }) => `flex items-center gap-2 px-2 h-9 rounded-lg text-sm ${isActive ? "bg-rose-50 text-[#EC4E5B] font-bold" : "text-gray-700 hover:bg-gray-100"} ${p.soon ? "italic text-gray-400" : ""}`}
                        data-testid={`nav-page-${(p.to || p.key).replace(/\//g, "-").replace(/^-/, "")}`}
                      >
                        <span className="w-4 text-[10px] font-black text-gray-400">{letterOf(pi)}</span>
                        {p.soon && <Hammer className="w-3 h-3" />}{p.lock && <Lock className="w-3 h-3 text-gray-400" />}
                        <span className="truncate">{p.label}</span>
                      </NavLink>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div className="pt-2 mt-2 border-t border-gray-200">
        <button type="button" onClick={() => setSettingsOpen((v) => !v)} className="w-full flex items-center gap-3 px-3 h-10 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-100" data-testid="nav-settings-toggle">
          <Settings className="w-4 h-4" /> <span className="flex-1 text-left">Impostazioni</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
        </button>
        {settingsOpen && (
          <div className="ml-3 pl-3 border-l-2 border-gray-200 mt-1">
            {ADMIN_SETTINGS_PAGES.map((p) => (
              <NavLink key={p.to} to={p.to} end onClick={onNavigate} className={({ isActive }) => `flex items-center px-2 h-9 rounded-lg text-sm ${isActive ? "bg-rose-50 text-[#EC4E5B] font-bold" : "text-gray-700 hover:bg-gray-100"}`} data-testid={`nav-settings-${p.to.replace(/\//g, "-").replace(/^-/, "")}`}>
                {p.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
